"use client";

import { useQuery } from "@tanstack/react-query";
import { useSearchParams, useRouter } from "next/navigation";
import { useCallback, useMemo, useRef } from "react";

import { getOngsStateCity } from "../ongs.service";

import type { OngsQueryParams } from "@/domain/Ongs/ongs.types";

export function useOngFilters() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const searchRef = useRef<{ clearInput: () => void }>(null);
  const stateSelectRef = useRef<{ clear: () => void }>(null);
  const citySelectRef = useRef<{ clear: () => void }>(null);

  const searchQuery = searchParams.get("search") ?? "";
  const stateQuery = searchParams.get("state") ?? "";
  const cityQuery = searchParams.get("city") ?? "";
  const pageQuery = searchParams.get("page") ?? "1";

  const queryParams = useMemo<OngsQueryParams>(
    () => ({
      pagination: { page: 1, pageSize: 100 },
      filters: stateQuery ? { state: { $eq: stateQuery } } : {}
    }),
    [stateQuery]
  );

  const { data } = useQuery({
    queryKey: ["ongsStateCity", stateQuery],
    queryFn: () => getOngsStateCity(queryParams),
    refetchOnWindowFocus: false,
    throwOnError: true
  });

  const { data: ongsStateCityData } = data || {};

  const ongsStateCity = useMemo(
    () => ongsStateCityData || [],
    [ongsStateCityData]
  );

  const optionsState = useMemo(() => {
    const unique = new Set(ongsStateCity.map((ong) => ong.state));
    return Array.from(unique, (state) => ({ label: state, value: state }));
  }, [ongsStateCity]);

  const optionsCity = useMemo(() => {
    const unique = new Set(ongsStateCity.map((ong) => ong.city));
    return Array.from(unique, (city) => ({ label: city, value: city }));
  }, [ongsStateCity]);

  const showAction =
    Number(pageQuery) !== 1 ||
    searchQuery !== "" ||
    stateQuery !== "" ||
    cityQuery !== "";

  const clearSelects = () => {
    if (stateSelectRef.current) {
      stateSelectRef.current.clear();
    }
    if (citySelectRef.current) {
      citySelectRef.current.clear();
    }
  };

  const handleClear = useCallback(() => {
    if (searchRef.current) {
      searchRef.current.clearInput();
    }
    clearSelects();
    const params = new URLSearchParams(searchParams.toString());
    params.delete("search");
    params.delete("state");
    params.delete("city");
    params.set("page", "1");
    router.push(`?${params.toString()}`, { scroll: false });
  }, [router, searchParams]);

  const onSearchSubmit = useCallback(
    (search: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set("search", search);
      params.set("page", "1");
      router.push(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const handleSelectStateOption = useCallback(
    (state: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set("state", state);
      params.delete("city");
      params.set("page", "1");
      router.push(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const handleSelectCityOption = useCallback(
    (city: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set("city", city);
      params.set("page", "1");
      router.push(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const handleClearStateOption = useCallback(() => {
    clearSelects();
    const params = new URLSearchParams(searchParams.toString());
    params.delete("state");
    params.delete("city");
    params.set("page", "1");

    router.push(`?${params.toString()}`, { scroll: false });
  }, [router, searchParams]);

  const handleClearCityOption = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete("city");
    params.set("page", "1");
    router.push(`?${params.toString()}`, { scroll: false });
  }, [router, searchParams]);

  const handleStateChange = useCallback(
    (state: string) => {
      handleSelectStateOption(state);
      citySelectRef.current?.clear();
    },
    [handleSelectStateOption]
  );

  return {
    searchRef,
    stateQuery,
    showAction,
    optionsCity,
    optionsState,
    citySelectRef,
    stateSelectRef,
    handleClear,
    onSearchSubmit,
    handleStateChange,
    handleSelectCityOption,
    handleClearStateOption,
    handleClearCityOption
  };
}
